import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Utils } from '../../utils';
import { Observable, Subscription } from 'rxjs';
declare var anychart: any

@Component({
  selector: 'app-radar-chart',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './radar-chart.component.html',
  styleUrl: './radar-chart.component.scss'
})
export class RadarChartComponent implements OnInit, OnDestroy {

  show = true;
  loading = false;
  loaderScripts = false;
  @ViewChild('chart') chartElement!: ElementRef;
  chart: any;

  private eventsSubscription?: Subscription;

  @Input() events?: Observable<void>;

  private _data: any[] = [];

  @Input()
  set data(data: any[]) {
    this._data = data;
    if (this.chartElement && this.loaderScripts) {
      this.apply();
    }

  }
  get data(): any[] {
    return this._data;
  }

  @Input() showLegend: boolean = true;

  constructor() {
  }

  async ngOnInit(): Promise<void> {
    const me = this;
    this.loading = true;

    await Utils.loadScript("file1", "https://cdn.anychart.com/releases/8.13.0/js/anychart-core.min.js")
      .then(async data => {
        await Utils.loadScript("file2", "https://cdn.anychart.com/releases/8.13.0/js/anychart-polar.min.js")
          .then(data => {
            me.loaderScripts = true;
            me.apply();
          })
          .catch(err => {
            console.error(err);
          });
      })
      .catch(err => {
        console.error(err);
      });

    if (this.events) {
      this.eventsSubscription = this.events.subscribe(() => {
        this.apply();
      });
    }

    // Listener para redimensionamiento de ventana
    window.addEventListener('resize', () => {
      if (this.chart && this.loaderScripts) {
        setTimeout(() => {
          this.apply();
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
      this.loaderScripts = false;
      this.chartElement.nativeElement.innerHTML = '';
      anychart = null;
      let file1 = document.getElementById("file1");
      let file2 = document.getElementById("file2");

      if (file1) document.body.removeChild(file1);
      if (file2) document.body.removeChild(file2);
      if (this.eventsSubscription)
        this.eventsSubscription.unsubscribe();

    }
    
    // Remover listener de redimensionamiento
    window.removeEventListener('resize', () => {});
  }

  private convertData(): any {
    let dataArray = [];

    const dimensions = this._data[0].dimensions;
    const isMobile = window.innerWidth <= 768;
    const isSmallMobile = window.innerWidth <= 480;
    
    for (let i = 0; i < this._data[0].dimensions.length; i++) {
      const dataNow = !this._data[0].hidden ? this._data[0] : { data: [] };
      const dataNext = !this._data[1].hidden ? this._data[1] : { data: [] };

      // Acortar nombres para móviles
      let dimensionName = dimensions[i].name;
      if (isSmallMobile) {
        // Nombres muy cortos para móviles pequeños
        dimensionName = this.getShortName(dimensionName, 4);
      } else if (isMobile) {
        // Nombres cortos para móviles
        dimensionName = this.getShortName(dimensionName, 6);
      }

      dataArray.push([dimensionName, dataNow.data[i] || 0, dataNext.data[i] || 0]);
    }
    return anychart.data.mapAsTable(dataArray);
  }

  private getShortName(fullName: string, maxLength: number): string {
    const shortNames: { [key: string]: string } = {
      'Salud y bienestar': 'Salud',
      'Diversión': 'Diversión',
      'Crecimiento Personal': 'Crecimiento',
      'Transformación': 'Transform',
      'Familia': 'Familia',
      'Círculos Sociales': 'Sociales',
      'Trabajo y Desarrollo Profesional': 'Trabajo',
      'Ingresos Económicos': 'Ingresos'
    };

    if (shortNames[fullName]) {
      return shortNames[fullName];
    }

    // Si no hay un nombre corto predefinido, cortar el original
    if (fullName.length > maxLength) {
      return fullName.substring(0, maxLength) + '...';
    }

    return fullName;
  }
  private apply() {
    if (!this._data || !this._data[0] || !this._data[0].dimensions) return;

    const dataArray = this.convertData();

    if (this.chart) {
      this.chart.removeAllSeries();
      this.chart.addSeries.apply(this.chart, dataArray);
    }
    else {

      this.chart = anychart.polar();
      this.chart
        .defaultSeriesType('polygon')
        .yAxis(true)
        .xScale('ordinal');

      this.chart.addSeries.apply(this.chart, dataArray);

      var scale = this.chart.yScale();
      scale.maximum(10);
      scale.minimum(0);
      scale.ticks().interval(1);

      // Configuración responsive para etiquetas
      const isMobile = window.innerWidth <= 768;
      const isSmallMobile = window.innerWidth <= 480;
      
      if (isSmallMobile) {
        // Configuración para móviles muy pequeños
        this.chart.labels().fontSize(7);
        this.chart.labels().fontWeight(400);
        this.chart.labels().padding(1, 1, 1, 1);
        this.chart.margin(15, 15, 15, 15);
        this.chart.padding(2, 2, 2, 2);
      } else if (isMobile) {
        // Configuración para móviles
        this.chart.labels().fontSize(9);
        this.chart.labels().fontWeight(500);
        this.chart.labels().padding(2, 2, 2, 2);
        this.chart.margin(12, 12, 12, 12);
        this.chart.padding(3, 3, 3, 3);
      } else {
        // Configuración para desktop
        this.chart.labels().fontSize(12);
        this.chart.labels().fontWeight(500);
        this.chart.labels().padding(5, 5, 5, 5);
      }

      this.chart
      .sortPointsByX(true);

      this.chart.container(this.chartElement.nativeElement);
      this.chart.draw();

      this.loading = false;
    }

    const colorNow = '#00D15A';
    const colorNext = '#ce0024';
    if (this._data.length == 1) {
      this.chart.getSeriesAt(0).normal().fill(this._data[0].name == 'Donde estás' ? colorNow : colorNext, 0.4);
      this.chart.getSeriesAt(0).selected().fill(this._data[0].name == 'Donde estás' ? colorNow : colorNext, 0.6);
      this.chart.getSeriesAt(0).name(this._data[0].name);
    }

    if (this._data.length == 2) {
      this.chart.getSeriesAt(0).normal().fill(colorNow, 0.4);
      this.chart.getSeriesAt(0).selected().fill(colorNow, 0.6);
      this.chart.getSeriesAt(0).name(this._data[0].name);

      this.chart.getSeriesAt(1).normal().fill(colorNext, 0.4);
      this.chart.getSeriesAt(1).selected().fill(colorNext, 0.6);
      this.chart.getSeriesAt(1).name(this._data[1].name);
    }

  }

  toggleSeries(index: number): void {
    if (this._data && this._data[index]) {
      this._data[index].hidden = !this._data[index].hidden;
      this.apply();
    }
  }

  getSeriesColor(index: number): string {
    if (!this._data || !this._data[index]) return '#ccc';
    
    const colorNow = '#00D15A';
    const colorNext = '#ce0024';
    
    if (this._data[index].name === 'Donde estás') {
      return colorNow;
    } else if (this._data[index].name === 'Donde quieres estar') {
      return colorNext;
    }
    
    // Colores por defecto basados en el índice
    return index === 0 ? colorNow : colorNext;
  }
}
